import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import type { TOC } from '@ember/component/template-only';
import { PhArrowsClockwise, PhCloudArrowUp } from 'ember-phosphor-icons';
import Button from 'tio-ui/components/button';
import ReSelect from 'tio-ui/components/select';
import FileUploader from '../file-uploader.gts';

export interface AttachmentClassificationOption {
  label: string;
  key: string;
}

interface AttachmentUploadSectionSignature {
  Args: {
    canUpload: boolean;
    showUploadSection: boolean;
    toggleUploadSection: () => void;
    selectedClassification: string;
    classificationDidChange: (key: string) => void;
    uploadFile: { perform: (file: unknown) => Promise<void>; isRunning: boolean };
    classificationOptions?: AttachmentClassificationOption[];
  };
}

const AttachmentUploadSection: TOC<AttachmentUploadSectionSignature> = <template>
  {{#if @canUpload}}
    {{#unless @showUploadSection}}
      <Button
        @intent="primary"
        @appearance="outlined"
        @icon={{PhCloudArrowUp}}
        @label={{t
          "common.tas.approval.drawer.attachments.upload_attachment"
          default="Upload Attachment"
        }}
        class="w-full"
        {{on "click" @toggleUploadSection}}
        data-test-upload-attachment-button
      />
    {{/unless}}
  {{/if}}

  {{#if @showUploadSection}}
    <div class="border app-divider-info rounded-lg tio-bg-surface shadow-sm">
      <div class="tio-row-between px-6 py-4 border-b app-divider">
        <div class="tio-row-tight">
          <PhCloudArrowUp @size="20" class="app-icon-info" />
          <h3 class="tio-text-base-semibold tio-fg-primary">
            {{t "tuition_assistance.program_details.upload_attachments"}}
          </h3>
        </div>
        <Button
          @appearance="minimal"
          @size="sm"
          @intent="default"
          {{on "click" @toggleUploadSection}}
          data-test-cancel-upload-button
        >
          {{t "common.cancel"}}
        </Button>
      </div>

      <div class="px-6 py-5 space-y-5">
        {{#if @classificationOptions}}
          <div>
            <label class="tio-text-sm-medium tio-fg-secondary block mb-2">
              {{t
                "tuition_assistance.program_details.courses.document_type"
                default="Document Type"
              }}
            </label>
            <ReSelect
              @items={{@classificationOptions}}
              @selected={{@selectedClassification}}
              @onChange={{@classificationDidChange}}
              @clearable={{false}}
            />
          </div>
        {{/if}}

        <div>
          <FileUploader @didUploadFile={{@uploadFile.perform}} as |uploader|>
            <input
              id={{uploader.uniqueId}}
              type="file"
              multiple="true"
              {{uploader.queue.selectFile}}
              class="hidden"
              disabled={{@uploadFile.isRunning}}
            />
            <label
              for={{uploader.uniqueId}}
              class="group app-dropzone block cursor-pointer rounded-lg
                {{if @uploadFile.isRunning 'pointer-events-none cursor-not-allowed'}}"
            >
              <div class="px-6 py-8 text-center">
                {{#if @uploadFile.isRunning}}
                  <PhArrowsClockwise @size="32" class="mx-auto mb-3 app-icon-info animate-spin" />
                  <p class="tio-text-sm-medium tio-fg-secondary">
                    {{t "file_upload.uploading"}}
                  </p>
                {{else}}
                  <PhCloudArrowUp @size="32" class="mx-auto mb-3 app-dropzone__icon" />
                  <div class="flex items-center justify-center gap-2 mb-2">
                    <span class="tio-text-sm-semibold app-dropzone__accent">
                      {{t "choose_file" default="Choose File"}}
                    </span>
                    <span class="text-sm tio-fg-muted">
                      {{t "file_upload.or_drag_and_drop"}}
                    </span>
                  </div>
                  <p class="tio-text-caption">
                    {{t
                      "tuition_assistance.program_details.courses.you_can_upload_multiple_attachments"
                    }}
                  </p>
                {{/if}}
              </div>
            </label>
          </FileUploader>
        </div>
      </div>
    </div>
  {{/if}}
</template>;

export default AttachmentUploadSection;
